import { useMemo } from 'react'
import styles from './SimulationSideCard.module.scss'
import { useTranslation } from 'next-i18next/pages'
import { EnergyType } from 'types/producer'
import SimulationRegistrationSideCardLayout from 'components/simulation-registration-side-card-layout/SimulationRegistrationSideCardLayout'
import classNames from 'classnames'
import { SimulationSideCardContentProps } from './types'
import useWindowSize from 'hooks/useWindowSize'
import { getCapacityState, getEnergyNameI18Key, getProducerLocation } from 'utils/producers'
import { CustomerFlows, InputGroupKeys } from 'store/customer/enums'
import { SimulationSteps } from 'features/simulation/types'
import PromoCode from 'components/promo-code/PromoCode'
import { Language } from 'types/language'
import { useGetProducersQuery } from 'store/api/bolt-api'
import { useStoreSelector } from 'hooks/store'
import { Heading, Icon, InlineMessage } from '@boltenergy-be/design-system'
import { SIMULATION_SIDE_CARD_BREAKPOINT } from './constants'
import { useGetLanguage } from 'hooks/useGetLanguage'
import useSelectInputGroup from 'hooks/useSelectInputGroup'

const SimulationSideCardContent = ({ openProducerDrawer }: SimulationSideCardContentProps) => {
  // Redux store
  const { selectedProducer, isLoading } = useStoreSelector((store) => store.app)
  const { currentStep } = useStoreSelector((store) => store.customer.flows[CustomerFlows.SIMULATION])
  const { needsGas } = useSelectInputGroup(InputGroupKeys.DELIVERY_ADDRESS)
  const inputsSimulation = useSelectInputGroup(InputGroupKeys.SIMULATION)

  // Redux queries
  const { data: producers } = useGetProducersQuery()

  // i18n
  const { t } = useTranslation(['simulationCard', 'common', 'producers', 'producer'])
  const language = useGetLanguage()

  // window size
  const { isTablet } = useWindowSize(SIMULATION_SIDE_CARD_BREAKPOINT)

  // Constants
  const producer = producers?.[selectedProducer]
  const capacityState = useMemo(() => getCapacityState(producer, inputsSimulation, true), [inputsSimulation, producer])

  return (
    <SimulationRegistrationSideCardLayout
      hideProducerLocation
      hideProducerImage={isTablet}
      loading={isLoading && !producer}
      producer={producer}
      className={styles['producer-info']}
    >
      <div>
        <strong className="text-sm weight-semibold">{t('youShallReceive', 'Jij ontvangt binnenkort:')}</strong>
      </div>

      <hr />

      <div className={classNames(styles.product, styles.electricity)}>
        <Icon name="electricity" />
        <div>
          {!!producer && (
            <>
              <strong className="text-xs weight-semibold">
                {t(`energyTypeAndLocation.${producer.energyType === EnergyType.BIO ? 'bio' : 'other'}`, {
                  energyType:
                    language === Language.FRENCH
                      ? t(getEnergyNameI18Key(producer.energyType)).toLowerCase()
                      : t(getEnergyNameI18Key(producer.energyType)),
                  location: getProducerLocation(language, producer?.location)
                })}
              </strong>
              <Heading as="h2" variant="h4" serif>
                {producer.name[language]}
              </Heading>
            </>
          )}

          {(capacityState.soldOut || currentStep === SimulationSteps.PROPOSITION) && (
            <SimulationRegistrationSideCardLayout.Action
              trailingIcon="chevronRight"
              className={styles['drawer-button']}
              onClick={openProducerDrawer}
            >
              {t('producer.chooseProducer', 'Kies zelf jouw energie-opwekker')}
            </SimulationRegistrationSideCardLayout.Action>
          )}

          {capacityState.soldOut && <InlineMessage type="negative">{t('producer:notEnoughCapacity')}</InlineMessage>}
        </div>
      </div>

      {needsGas && (
        <>
          <hr />

          <div className={classNames(styles.product, styles.gas)}>
            <Icon name="gas" />
            <div>
              <strong className="text-xs weight-semibold">
                {currentStep === SimulationSteps.PROPOSITION
                  ? t('common:gasFromMarket', 'Gas van de groothandelsmarkt')
                  : t('common:gas', 'Gas')}
              </strong>
              {currentStep !== SimulationSteps.PROPOSITION && <p className="text-xs">{t('producer.gas.warning')}</p>}
            </div>
          </div>
        </>
      )}

      {currentStep === SimulationSteps.PROPOSITION && (
        <>
          <hr />

          <div className={styles.promo}>
            <PromoCode variant="side-card" className={styles['promo-line']} />
          </div>
        </>
      )}
    </SimulationRegistrationSideCardLayout>
  )
}

export default SimulationSideCardContent
